import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CreditCard, 
  Fuel, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Store,
  Calendar,
  PoundSterling,
  Receipt,
  Banknote,
  X,
  ChevronDown,
  ChevronUp,
  Calculator,
  Edit2,
  History,
  FileText,
  Eye,
  Link2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

export const FuelCardBalancesPage = () => {
  const { user, isAdmin } = useAuth();
  const { stores, selectedStore } = useStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Link to Statement Mode - when coming from Purchases page
  const linkToStatementId = searchParams.get('link_to_statement');
  const [linkInvoice, setLinkInvoice] = useState(null);
  const [linkMode, setLinkMode] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [balances, setBalances] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Receive Payment Dialog
  const [receivePaymentOpen, setReceivePaymentOpen] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  // Key Fuels sub-item selections: { 'txn_id': ['diesel', 'non_diesel', 'shop'] }
  const [selectedKeyFuelsItems, setSelectedKeyFuelsItems] = useState({});
  // Bunkering grade selections: { 'txn_id': [0, 1, 2] } - array of grade indices
  const [selectedBunkeringGrades, setSelectedBunkeringGrades] = useState({});
  const [paymentForm, setPaymentForm] = useState({
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    statement_reference: '',
    statement_amount: '',
    bank_reference: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Dynamic fee state for settlement calculation
  const [settlementFees, setSettlementFees] = useState({
    handling_fee: 0,
    merchant_fee_fuel: 0,
    merchant_fee_shop: 0
  });
  
  // Separate state for fee inputs (allows free text editing)
  const [handlingFeeInput, setHandlingFeeInput] = useState('0.00');
  const [merchantFeeFuelInput, setMerchantFeeFuelInput] = useState('0.00');
  const [merchantFeeShopInput, setMerchantFeeShopInput] = useState('0.00');
  
  // Global fuel card settings (for fee rates)
  const [globalFuelCardSettings, setGlobalFuelCardSettings] = useState([]);
  
  // Store-specific fuel card settings (for handling fees)
  const [storeFuelCardSettings, setStoreFuelCardSettings] = useState([]);
  
  // Expanded card sections
  const [expandedCards, setExpandedCards] = useState({
    non_bunkering: true,
    bunkering: true,
    key_fuels: true
  });
  
  // Main tab state: 'balances' or 'history'
  const [activeTab, setActiveTab] = useState('balances');
  
  // Settlement detail dialog
  const [settlementDetailOpen, setSettlementDetailOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [settlementDetailLoading, setSettlementDetailLoading] = useState(false);

  useEffect(() => {
    if (stores.length > 0) {
      const fuelStores = stores.filter(s => s.store_type === 'fuel_station');
      if (fuelStores.length > 0) {
        const storeToSelect = selectedStore?.store_type === 'fuel_station' 
          ? selectedStore 
          : fuelStores[0];
        setSelectedStoreId(storeToSelect.id);
      }
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchBalances();
      fetchSettlements();
      fetchGlobalFuelCardSettings();
      fetchStoreFuelCardSettings();
    }
  }, [selectedStoreId]);

  // Fetch invoice details when in link mode
  useEffect(() => {
    if (linkToStatementId) {
      setLinkMode(true);
      fetchLinkInvoice(linkToStatementId);
    } else {
      setLinkMode(false);
      setLinkInvoice(null);
    }
  }, [linkToStatementId]);

  const fetchLinkInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/purchases/${invoiceId}`);
      setLinkInvoice(response.data);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      toast.error('Failed to load invoice details');
      navigate('/purchases');
    }
  };

  const fetchGlobalFuelCardSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const settings = response.data;
      setGlobalFuelCardSettings(settings.fuel_card_types || []);
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
    }
  };
  
  const fetchStoreFuelCardSettings = async () => {
    try {
      const response = await axios.get(`${API}/stores/${selectedStoreId}`);
      const store = response.data;
      setStoreFuelCardSettings(store.active_fuel_cards || []);
    } catch (error) {
      console.error('Failed to fetch store settings:', error);
    }
  };

  const fetchBalances = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/fuel-cards/balances`, {
        params: { store_id: selectedStoreId, is_settled: false }
      });
      setBalances(response.data || []);
    } catch (error) {
      console.error('Failed to fetch fuel card balances:', error);
      toast.error('Failed to load fuel card balances');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlements = async () => {
    try {
      const response = await axios.get(`${API}/fuel-cards/settlements`, {
        params: { store_id: selectedStoreId }
      });
      setSettlements(response.data || []);
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
    }
  };
  
  const fetchSettlementDetail = async (settlementId) => {
    try {
      setSettlementDetailLoading(true);
      const response = await axios.get(`${API}/fuel-cards/settlements/${settlementId}`);
      setSelectedSettlement(response.data);
      setSettlementDetailOpen(true);
    } catch (error) {
      console.error('Failed to fetch settlement details:', error);
      toast.error('Failed to load settlement details');
    } finally {
      setSettlementDetailLoading(false);
    }
  };

  // Group balances by category and card name
  const groupedBalances = useMemo(() => {
    const groups = {
      non_bunkering: {},
      bunkering: {},
      key_fuels: {},
      amex: {},
      voucher: {}
    };

    balances.forEach(balance => {
      const category = balance.card_category || 'non_bunkering';
      const cardName = balance.card_name || 'Unknown';
      
      if (!groups[category]) groups[category] = {};
      if (!groups[category][cardName]) {
        groups[category][cardName] = [];
      }
      groups[category][cardName].push(balance);
    });

    return groups;
  }, [balances]);

  // Calculate totals
  const totals = useMemo(() => {
    const result = {
      non_bunkering: 0,
      bunkering: 0,
      key_fuels: 0,
      amex: 0,
      voucher: 0
    };

    balances.forEach(b => {
      const category = b.card_category || 'non_bunkering';
      result[category] = (result[category] || 0) + (b.expected_payment || 0);
    });

    result.grand_total = result.non_bunkering + result.bunkering + result.key_fuels + result.amex + result.voucher;
    return result;
  }, [balances]);

  const handleTransactionSelect = (transactionId, checked) => {
    if (checked) {
      setSelectedTransactions(prev => [...prev, transactionId]);
    } else {
      setSelectedTransactions(prev => prev.filter(id => id !== transactionId));
      // Also clear Key Fuels sub-items for this transaction
      setSelectedKeyFuelsItems(prev => {
        const newItems = { ...prev };
        delete newItems[transactionId];
        return newItems;
      });
    }
  };

  // Handle Bunkering grade selection (by grade index within a transaction)
  const handleBunkeringGradeSelect = (transactionId, gradeIndex, checked) => {
    setSelectedBunkeringGrades(prev => {
      const currentGrades = prev[transactionId] || [];
      let newGrades;
      
      if (checked) {
        newGrades = [...new Set([...currentGrades, gradeIndex])];
        // Also ensure the main transaction is selected
        if (!selectedTransactions.includes(transactionId)) {
          setSelectedTransactions(p => [...p, transactionId]);
        }
      } else {
        newGrades = currentGrades.filter(idx => idx !== gradeIndex);
      }
      
      // If no grades selected, remove the transaction from selections
      if (newGrades.length === 0) {
        setSelectedTransactions(p => p.filter(id => id !== transactionId));
        const result = { ...prev };
        delete result[transactionId];
        return result;
      }
      
      return { ...prev, [transactionId]: newGrades };
    });
  };

  // Handle Key Fuels sub-item selection (by grade index for full granularity)
  const handleKeyFuelsSubItemSelect = (transactionId, gradeIndex, checked) => {
    setSelectedKeyFuelsItems(prev => {
      const currentItems = prev[transactionId] || [];
      let newItems;
      
      if (checked) {
        newItems = [...new Set([...currentItems, gradeIndex])];
        // Also ensure the main transaction is selected
        if (!selectedTransactions.includes(transactionId)) {
          setSelectedTransactions(p => [...p, transactionId]);
        }
      } else {
        newItems = currentItems.filter(idx => idx !== gradeIndex);
      }
      
      // If no sub-items selected, remove the transaction from selections
      if (newItems.length === 0) {
        setSelectedTransactions(p => p.filter(id => id !== transactionId));
        const result = { ...prev };
        delete result[transactionId];
        return result;
      }
      
      return { ...prev, [transactionId]: newItems };
    });
  };

  const handleSelectAllForCard = (cardName, category, checked) => {
    const cardTransactions = groupedBalances[category]?.[cardName] || [];
    const transactionIds = cardTransactions.map(t => t.id);
    
    if (checked) {
      setSelectedTransactions(prev => [...new Set([...prev, ...transactionIds])]);
      
      // For Bunkering, select all grade indices
      if (category === 'bunkering') {
        const newBunkeringGrades = {};
        cardTransactions.forEach(t => {
          const txns = t.transactions || [];
          if (txns.length > 0) {
            newBunkeringGrades[t.id] = txns.map((_, idx) => idx);
          }
        });
        setSelectedBunkeringGrades(prev => ({ ...prev, ...newBunkeringGrades }));
      }
      
      // For Key Fuels, select all grade indices (not type-based)
      if (category === 'key_fuels') {
        const newKeyFuelsItems = {};
        cardTransactions.forEach(t => {
          const txns = t.transactions || [];
          const hasShop = (t.shop_items_amount || 0) > 0;
          // Build indices: fuel grades + shop (if exists)
          const indices = txns.map((_, idx) => idx);
          if (hasShop) {
            indices.push('shop'); // Special marker for shop
          }
          if (indices.length > 0) {
            newKeyFuelsItems[t.id] = indices;
          }
        });
        setSelectedKeyFuelsItems(prev => ({ ...prev, ...newKeyFuelsItems }));
      }
    } else {
      setSelectedTransactions(prev => prev.filter(id => !transactionIds.includes(id)));
      
      // Clear Bunkering grades
      if (category === 'bunkering') {
        setSelectedBunkeringGrades(prev => {
          const newGrades = { ...prev };
          transactionIds.forEach(id => delete newGrades[id]);
          return newGrades;
        });
      }
      
      // Clear Key Fuels sub-items
      if (category === 'key_fuels') {
        setSelectedKeyFuelsItems(prev => {
          const newItems = { ...prev };
          transactionIds.forEach(id => delete newItems[id]);
          return newItems;
        });
      }
    }
  };

  const getSelectedTransactionDetails = useCallback(() => {
    const selected = balances.filter(b => selectedTransactions.includes(b.id));
    const cardNames = [...new Set(selected.map(t => t.card_name))];
    const categories = [...new Set(selected.map(t => t.card_category))];
    
    // Calculate totals based on category
    let totalAmount = 0;
    let totalVolume = 0;
    let dieselAmount = 0;
    let dieselVolume = 0;
    let nonDieselAmount = 0;
    let nonDieselVolume = 0;
    let shopItemsAmount = 0;
    
    selected.forEach(t => {
      const isKeyFuels = t.card_category === 'key_fuels';
      const isBunkering = t.card_category === 'bunkering';
      const selectedSubItems = selectedKeyFuelsItems[t.id] || [];
      const selectedBunkGrades = selectedBunkeringGrades[t.id] || [];
      
      // For Key Fuels, only include selected grades
      if (isKeyFuels && selectedSubItems.length > 0) {
        const txns = t.transactions || [];
        selectedSubItems.forEach(idx => {
          if (idx === 'shop') {
            // Shop item
            const shopAmt = t.shop_items_amount || 0;
            shopItemsAmount += shopAmt;
            totalAmount += shopAmt;
          } else if (typeof idx === 'number' && txns[idx]) {
            // Fuel grade by index
            const txn = txns[idx];
            const isDiesel = txn.grade_name === 'Diesel';
            const amt = txn.calculated_amount || txn.amount || 0;
            const vol = txn.volume || 0;
            
            if (isDiesel) {
              dieselAmount += amt;
              dieselVolume += vol;
            } else {
              nonDieselAmount += amt;
              nonDieselVolume += vol;
            }
            totalAmount += amt;
            totalVolume += vol;
          }
        });
      } 
      // For Bunkering, only include selected grades
      else if (isBunkering && selectedBunkGrades.length > 0) {
        const txns = t.transactions || [];
        selectedBunkGrades.forEach(idx => {
          if (txns[idx]) {
            const txn = txns[idx];
            const costAmount = txn.cost_amount || txn.amount || 0;
            const vol = txn.volume || 0;
            
            totalAmount += costAmount;
            totalVolume += vol;
            dieselVolume += vol; // Bunkering is always fuel
          }
        });
      }
      // For Bunkering without grade selection (legacy/full select)
      else if (isBunkering && selectedBunkGrades.length === 0) {
        totalAmount += t.total_amount || t.expected_payment || 0;
        const txnVolume = t.transactions?.reduce((sum, txn) => sum + (txn.volume || 0), 0) || 0;
        totalVolume += t.total_volume || txnVolume;
        dieselVolume += t.total_volume || txnVolume;
      }
      // Non Key Fuels / Non Bunkering - use full amount
      else if (!isKeyFuels && !isBunkering) {
        totalAmount += t.total_amount || t.expected_payment || 0;
        const txnVolume = t.transactions?.reduce((sum, txn) => sum + (txn.volume || 0), 0) || 0;
        totalVolume += t.total_volume || txnVolume;
      }
    });
    
    // Get the category-specific settings for fee rates
    const primaryCategory = categories[0];
    
    // Look for card config from store's active fuel cards (has the actual handling fees)
    let cardConfig = null;
    for (const cardName of cardNames) {
      const storeCard = storeFuelCardSettings.find(fc => fc.card_name === cardName);
      if (storeCard) {
        cardConfig = storeCard;
        break;
      }
    }
    
    // Fallback to global fuel card settings
    if (!cardConfig) {
      cardConfig = globalFuelCardSettings.find(fc => 
        fc.category === primaryCategory && cardNames.includes(fc.name)
      ) || globalFuelCardSettings.find(fc => fc.category === primaryCategory);
    }
    
    // Calculate default fees based on category
    let defaultHandlingFee = 0;
    let defaultMerchantFeeFuel = 0;
    let defaultMerchantFeeShop = 0;
    
    if (primaryCategory === 'bunkering') {
      // Bunkering: Handling Fee = Total Volume × handling_fee_per_litre (in pence, convert to £)
      const handlingFeePerLitre = (cardConfig?.handling_fee_per_litre || 0) / 100; // pence to £
      defaultHandlingFee = totalVolume * handlingFeePerLitre;
    } else if (primaryCategory === 'key_fuels') {
      // Key Fuels: Handling Fee only for Diesel grades
      const handlingFeePerLitre = (cardConfig?.diesel_handling_fee_per_litre || 0) / 100;
      defaultHandlingFee = dieselVolume * handlingFeePerLitre;  // Only diesel volume
      
      // Merchant fees
      const nonDieselMerchantPercent = cardConfig?.other_grades_merchant_fee_percent || 3;
      const shopMerchantPercent = cardConfig?.shop_items_merchant_fee_percent || 10;
      defaultMerchantFeeFuel = nonDieselAmount * (nonDieselMerchantPercent / 100);
      defaultMerchantFeeShop = shopItemsAmount * (shopMerchantPercent / 100);
    }
    
    return { 
      selected, 
      totalAmount, 
      totalVolume,
      dieselAmount,
      dieselVolume,
      nonDieselAmount,
      nonDieselVolume,
      shopItemsAmount,
      cardNames, 
      categories,
      primaryCategory,
      defaultHandlingFee,
      defaultMerchantFeeFuel,
      defaultMerchantFeeShop,
      cardConfig
    };
  }, [balances, selectedTransactions, selectedKeyFuelsItems, selectedBunkeringGrades, globalFuelCardSettings, storeFuelCardSettings]);
  
  // Reset fees when transactions change
  useEffect(() => {
    if (selectedTransactions.length > 0) {
      const details = getSelectedTransactionDetails();
      setSettlementFees({
        handling_fee: details.defaultHandlingFee,
        merchant_fee_fuel: details.defaultMerchantFeeFuel,
        merchant_fee_shop: details.defaultMerchantFeeShop
      });
      setHandlingFeeInput(details.defaultHandlingFee.toFixed(2));
      setMerchantFeeFuelInput(details.defaultMerchantFeeFuel.toFixed(2));
      setMerchantFeeShopInput(details.defaultMerchantFeeShop.toFixed(2));
    }
  }, [selectedTransactions, selectedKeyFuelsItems, selectedBunkeringGrades, getSelectedTransactionDetails]);
  
  // Calculate final settlement amount
  const calculateSettlementAmount = useCallback(() => {
    const details = getSelectedTransactionDetails();
    const { primaryCategory, totalAmount, dieselAmount, nonDieselAmount, shopItemsAmount } = details;
    
    if (primaryCategory === 'bunkering') {
      // Bunkering: Settlement = Total Amount + Handling Fee
      return totalAmount + settlementFees.handling_fee;
    } else if (primaryCategory === 'key_fuels') {
      // Key Fuels: Settlement = Subtotal + Handling Fee - Merchant Fees
      const subtotal = dieselAmount + nonDieselAmount + shopItemsAmount;
      return subtotal + settlementFees.handling_fee - settlementFees.merchant_fee_fuel - settlementFees.merchant_fee_shop;
    } else {
      // Non-bunkering: Direct retail amount (expected_payment)
      return details.selected.reduce((sum, t) => sum + (t.expected_payment || 0), 0);
    }
  }, [getSelectedTransactionDetails, settlementFees]);

  const handleReceivePayment = async () => {
    if (selectedTransactions.length === 0) {
      toast.error('Please select at least one transaction');
      return;
    }
    
    if (!paymentForm.statement_reference) {
      toast.error('Please enter statement reference');
      return;
    }
    
    if (!paymentForm.statement_amount) {
      toast.error('Please enter statement amount');
      return;
    }

    setSubmitting(true);
    try {
      const details = getSelectedTransactionDetails();
      
      const response = await axios.post(`${API}/fuel-cards/settlements`, {
        store_id: selectedStoreId,
        transaction_ids: selectedTransactions,
        payment_date: paymentForm.payment_date,
        statement_reference: paymentForm.statement_reference,
        statement_amount: parseFloat(paymentForm.statement_amount),
        bank_reference: paymentForm.bank_reference,
        // Include fee breakdown for audit
        settlement_fees: {
          handling_fee: settlementFees.handling_fee,
          merchant_fee_fuel: settlementFees.merchant_fee_fuel,
          merchant_fee_shop: settlementFees.merchant_fee_shop,
          calculated_settlement: calculateSettlementAmount()
        }
      });

      toast.success(`Settlement created! Bank balance credited: ${formatCurrency(response.data.bank_balance_credited)}`);
      
      // Reset form
      setReceivePaymentOpen(false);
      setSelectedTransactions([]);
      setPaymentForm({
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        statement_reference: '',
        statement_amount: '',
        bank_reference: ''
      });
      setSettlementFees({
        handling_fee: 0,
        merchant_fee_fuel: 0,
        merchant_fee_shop: 0
      });
      
      // Refresh data
      fetchBalances();
      fetchSettlements();
    } catch (error) {
      console.error('Failed to create settlement:', error);
      toast.error(error.response?.data?.detail || 'Failed to create settlement');
    } finally {
      setSubmitting(false);
    }
  };

  // Link selected transactions to a Fuel statement/credit note
  const handleLinkToStatement = async () => {
    if (selectedTransactions.length === 0) {
      toast.error('Please select at least one transaction');
      return;
    }

    if (!linkInvoice) {
      toast.error('No invoice selected for linking');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/purchases/fuel-transactions/reconcile`, {
        credit_note_id: linkInvoice.id,
        credit_note_number: linkInvoice.invoice_number,
        transaction_ids: selectedTransactions,
        variance_notes: ''
      });

      toast.success(`Linked ${selectedTransactions.length} transactions to statement ${linkInvoice.invoice_number}`);
      
      // Navigate back to Purchases page
      navigate('/purchases');
    } catch (error) {
      console.error('Failed to link transactions:', error);
      toast.error(error.response?.data?.detail || 'Failed to link transactions to statement');
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel link mode and go back to Purchases
  const handleCancelLinkMode = () => {
    navigate('/purchases');
  };

  const fuelStores = stores.filter(s => s.store_type === 'fuel_station');
  const transactionDetails = getSelectedTransactionDetails();
  const calculatedSettlement = calculateSettlementAmount();

  const categoryLabels = {
    non_bunkering: { label: 'Non Bunkering Cards', color: 'blue', desc: 'Retail price - paid weekly' },
    bunkering: { label: 'Bunkering Cards', color: 'orange', desc: 'Cost + fee - paid weekly' },
    key_fuels: { label: 'Key Fuels', color: 'green', desc: 'Mixed pricing - paid monthly' },
    amex: { label: 'AMEX', color: 'purple', desc: 'Weekly settlement' },
    voucher: { label: 'Voucher', color: 'emerald', desc: 'Settlement on redemption' }
  };

  const renderCategorySection = (category) => {
    const config = categoryLabels[category];
    const cards = groupedBalances[category] || {};
    const cardNames = Object.keys(cards);
    
    if (cardNames.length === 0) return null;
    
    const isExpanded = expandedCards[category];
    const categoryTotal = Object.values(cards).flat().reduce((sum, t) => sum + (t.expected_payment || 0), 0);

    return (
      <Card key={category} className={`border-${config.color}-500/30`}>
        <CardHeader 
          className={`bg-${config.color}-500/5 border-b border-${config.color}-500/20 cursor-pointer`}
          onClick={() => setExpandedCards(prev => ({ ...prev, [category]: !prev[category] }))}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className={`h-5 w-5 text-${config.color}-500`} />
              <div>
                <CardTitle className={`text-${config.color}-600`} style={{ fontFamily: 'Barlow Condensed' }}>
                  {config.label}
                </CardTitle>
                <CardDescription className="text-xs">{config.desc}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-lg font-bold text-${config.color}-600`}>
                {formatCurrency(categoryTotal)}
              </span>
              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </div>
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="pt-4 space-y-4">
            {cardNames.map(cardName => {
              const transactions = cards[cardName];
              const cardTotal = transactions.reduce((sum, t) => sum + (t.expected_payment || 0), 0);
              const allSelected = transactions.every(t => selectedTransactions.includes(t.id));
              const someSelected = transactions.some(t => selectedTransactions.includes(t.id));
              
              return (
                <div key={cardName} className="border rounded-lg overflow-hidden">
                  <div className={`bg-${config.color}-500/10 px-4 py-2 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onCheckedChange={(checked) => handleSelectAllForCard(cardName, category, checked)}
                        data-testid={`select-all-${cardName.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <span className="font-semibold">{cardName}</span>
                      <span className="text-xs text-muted-foreground">
                        ({transactions.length} transaction{transactions.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <span className="font-bold">{formatCurrency(cardTotal)}</span>
                  </div>
                  
                  <div className="divide-y">
                    {transactions.map(transaction => (
                      <div 
                        key={transaction.id} 
                        className={`px-4 py-2 flex items-center gap-4 hover:bg-slate-50 transition-colors ${
                          selectedTransactions.includes(transaction.id) ? 'bg-primary/5' : ''
                        }`}
                      >
                        <Checkbox
                          checked={selectedTransactions.includes(transaction.id)}
                          onCheckedChange={(checked) => handleTransactionSelect(transaction.id, checked)}
                          data-testid={`select-tx-${transaction.id}`}
                        />
                        <div className="flex-1 text-sm">
                          {/* Non-bunkering display - Date and Expected only */}
                          {category === 'non_bunkering' && (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-muted-foreground">Date:</span>{' '}
                                <span className="font-medium">{transaction.sales_date}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-muted-foreground">Expected:</span>{' '}
                                <span className="font-mono font-bold">{formatCurrency(transaction.expected_payment)}</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Bunkering display - table format like Key Fuels */}
                          {category === 'bunkering' && (() => {
                            const txns = transaction.transactions || [];
                            const selectedGradeIndices = selectedBunkeringGrades[transaction.id] || [];
                            
                            // For bunkering cards, we want to show the daily cost price
                            // This is already calculated as costPerLitre from the transaction data
                            
                            // Build rows from transactions
                            const rows = txns.map((t, idx) => {
                              const volume = t.volume || 0;
                              const costAmount = t.cost_amount || t.amount || 0;
                              const retailAmount = volume * (t.selling_price || 0) / 100;
                              // Cost per litre - this is the daily cost price used for bunkering calculation
                              const costPerLitre = t.cost_price_pence || t.cost_price || (volume > 0 ? (costAmount * 100 / volume) : 0);
                              const netAmount = costAmount / 1.2;
                              const vatAmount = costAmount - netAmount;
                              
                              return {
                                idx,
                                gradeName: t.grade_name || 'Fuel',
                                volume,
                                retail: retailAmount,
                                costPerLitre,
                                net: netAmount,
                                vat: vatAmount,
                                total: costAmount
                              };
                            });
                            
                            return (
                              <div className="w-full">
                                {/* Date header */}
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-muted-foreground text-xs">Date:</span>
                                  <span className="font-medium">{transaction.sales_date}</span>
                                </div>
                                
                                {/* Table with column headers */}
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-muted-foreground text-xs border-b border-border/50">
                                      <th className="text-left py-1 pl-2 w-32">Grade</th>
                                      <th className="text-right py-1 w-20">Volume</th>
                                      <th className="text-right py-1 w-20">Retail</th>
                                      <th className="text-right py-1 w-20">Cost</th>
                                      <th className="text-right py-1 w-20">Net Cost</th>
                                      <th className="text-right py-1 w-20">VAT</th>
                                      <th className="text-right py-1 w-24">Total Cost</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((row) => {
                                      const isSelected = selectedGradeIndices.includes(row.idx);
                                      
                                      return (
                                        <tr 
                                          key={row.idx}
                                          className={isSelected ? 'bg-primary/5' : ''}
                                        >
                                          {/* Grade with Checkbox */}
                                          <td className="py-1.5">
                                            <div className="flex items-center gap-2">
                                              <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={(checked) => handleBunkeringGradeSelect(transaction.id, row.idx, checked)}
                                                className="border-orange-500"
                                                data-testid={`select-bunkering-grade-${transaction.id}-${row.idx}`}
                                              />
                                              <Fuel className="h-3.5 w-3.5 text-orange-500" />
                                              <span className="font-medium text-orange-600">{row.gradeName}</span>
                                            </div>
                                          </td>
                                          
                                          {/* Volume */}
                                          <td className="text-right font-mono py-1.5">
                                            {row.volume.toFixed(2)} L
                                          </td>
                                          
                                          {/* Retail */}
                                          <td className="text-right font-mono py-1.5 text-muted-foreground">
                                            {formatCurrency(row.retail)}
                                          </td>
                                          
                                          {/* Cost (Daily Cost Price) */}
                                          <td className="text-right font-mono py-1.5 text-cyan-600">
                                            {row.costPerLitre.toFixed(2)}p
                                          </td>
                                          
                                          {/* Net */}
                                          <td className="text-right font-mono py-1.5 text-green-600">
                                            {formatCurrency(row.net)}
                                          </td>
                                          
                                          {/* VAT */}
                                          <td className="text-right font-mono py-1.5 text-green-600">
                                            {formatCurrency(row.vat)}
                                          </td>
                                          
                                          {/* Total */}
                                          <td className="text-right font-mono font-bold py-1.5 text-green-600">
                                            {formatCurrency(row.total)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                          
                          {/* Key Fuels display - table format with date like Bunkering */}
                          {category === 'key_fuels' && (() => {
                            const txns = transaction.transactions || [];
                            const shopAmt = transaction.shop_items_amount || 0;
                            
                            const selectedItems = selectedKeyFuelsItems[transaction.id] || [];
                            const hasShop = shopAmt > 0;
                            
                            // Build all rows (fuel grades + shop) with indices
                            // Backend already calculates:
                            // - Diesel: Volume × Daily Cost Price (stored in t.amount)
                            // - Others: Volume × Retail Price (stored in t.amount)
                            const rows = [];
                            
                            // Process fuel grades - use amounts directly from backend
                            txns.forEach((t, idx) => {
                              const isDiesel = t.grade_name === 'Diesel';
                              
                              // Backend stores the correct calculated amount:
                              // - Diesel: Volume × Cost Price
                              // - Others: Volume × Retail Price
                              // Use calculated_amount first (correct value), fallback to amount
                              const amount = t.calculated_amount || t.amount || 0;
                              
                              // Calculate retail for display (Volume × Retail Price)
                              const retailAmount = (t.volume || 0) * (t.retail_price || t.selling_price || 0) / 100;
                              
                              // Get cost price per litre (in pence)
                              const costPricePerLitre = t.cost_price || t.cost_price_pence || 0;
                              
                              // VAT breakdown (Amount includes VAT)
                              const netAmount = amount / 1.2;
                              const vatAmount = amount - netAmount;
                              
                              rows.push({
                                type: 'fuel',
                                selectionKey: idx, // Use index for selection
                                isDiesel,
                                gradeName: t.grade_name,
                                volume: t.volume || 0,
                                retail: retailAmount,
                                costPerLitre: costPricePerLitre,
                                net: netAmount,
                                vat: vatAmount,
                                total: amount,
                                priceType: isDiesel ? 'Cost' : 'Retail'
                              });
                            });
                            
                            // Add shop row if exists
                            if (hasShop) {
                              const shopNet = shopAmt / 1.2;
                              const shopVat = shopAmt - shopNet;
                              rows.push({
                                type: 'shop',
                                selectionKey: 'shop', // Special key for shop
                                gradeName: 'Shop Sales',
                                volume: null,
                                retail: null,
                                costPerLitre: null,
                                net: shopNet,
                                vat: shopVat,
                                total: shopAmt
                              });
                            }
                            
                            return (
                              <div className="w-full">
                                {/* Date header like Bunkering */}
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-muted-foreground text-xs">Date:</span>
                                  <span className="font-medium">{transaction.sales_date}</span>
                                </div>
                                
                                {/* Table with column headers */}
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-muted-foreground text-xs border-b border-border/50">
                                      <th className="text-left py-1 pl-2 w-40">Grade</th>
                                      <th className="text-right py-1 w-20">Volume</th>
                                      <th className="text-right py-1 w-20">Retail</th>
                                      <th className="text-right py-1 w-20">Cost/L</th>
                                      <th className="text-right py-1 w-20">Net</th>
                                      <th className="text-right py-1 w-20">VAT</th>
                                      <th className="text-right py-1 w-24">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((row, idx) => {
                                      const isSelected = selectedItems.includes(row.selectionKey);
                                      
                                      // Color schemes
                                      const colorClass = row.isDiesel 
                                        ? 'text-blue-600' 
                                        : row.type === 'shop' 
                                          ? 'text-purple-600' 
                                          : 'text-amber-600';
                                      
                                      return (
                                        <tr 
                                          key={idx} 
                                          className={`${isSelected ? 'bg-primary/5' : ''}`}
                                        >
                                          {/* Grade with Checkbox */}
                                          <td className="py-1.5">
                                            <div className="flex items-center gap-2">
                                              <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={(checked) => handleKeyFuelsSubItemSelect(transaction.id, row.selectionKey, checked)}
                                                className={row.isDiesel ? 'border-blue-500' : row.type === 'shop' ? 'border-purple-500' : 'border-amber-500'}
                                                data-testid={`select-keyfuels-${transaction.id}-${row.selectionKey}`}
                                              />
                                              {row.type === 'shop' ? (
                                                <Receipt className={`h-3.5 w-3.5 ${colorClass}`} />
                                              ) : (
                                                <Fuel className={`h-3.5 w-3.5 ${colorClass}`} />
                                              )}
                                              <span className={`font-medium ${colorClass}`}>{row.gradeName}</span>
                                              <span className={`text-xs ${colorClass}`}>({row.priceType || 'Retail'})</span>
                                            </div>
                                          </td>
                                          
                                          {/* Volume */}
                                          <td className="text-right font-mono py-1.5">
                                            {row.volume !== null ? `${row.volume.toFixed(2)} L` : '-'}
                                          </td>
                                          
                                          {/* Retail (for reference) */}
                                          <td className="text-right font-mono py-1.5 text-muted-foreground">
                                            {row.retail !== null ? formatCurrency(row.retail) : '-'}
                                          </td>
                                          
                                          {/* Cost/L */}
                                          <td className="text-right font-mono py-1.5">
                                            {row.costPerLitre !== null ? `${row.costPerLitre.toFixed(2)}p` : '-'}
                                          </td>
                                          
                                          {/* Net (Ex-VAT) */}
                                          <td className={`text-right font-mono py-1.5 ${colorClass}`}>
                                            {formatCurrency(row.net)}
                                          </td>
                                          
                                          {/* VAT */}
                                          <td className={`text-right font-mono py-1.5 ${colorClass}`}>
                                            {formatCurrency(row.vat)}
                                          </td>
                                          
                                          {/* Total */}
                                          <td className={`text-right font-mono font-bold py-1.5 ${colorClass}`}>
                                            {formatCurrency(row.total)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                          
                          {/* AMEX/Voucher display - simple */}
                          {(category === 'amex' || category === 'voucher') && (
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <span className="text-muted-foreground">Date:</span>{' '}
                                <span className="font-medium">{transaction.sales_date}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Amount:</span>{' '}
                                <span className="font-mono">{formatCurrency(transaction.total_amount)}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-muted-foreground">Expected:</span>{' '}
                                <span className="font-mono font-bold">{formatCurrency(transaction.expected_payment)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        )}
      </Card>
    );
  };

  if (fuelStores.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Fuel className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Fuel Stations</h2>
            <p className="text-muted-foreground">
              Fuel card balances are only available for fuel station stores.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`p-4 md:p-6 space-y-6 ${selectedTransactions.length > 0 ? 'pb-24' : ''}`}>
      {/* Link Mode Banner */}
      {linkMode && linkInvoice && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <FileText className="h-8 w-8 text-primary" />
            <div>
              <p className="font-semibold text-primary">Linking Transactions to Statement</p>
              <p className="text-sm text-muted-foreground">
                Credit Note: <span className="font-mono font-bold">{linkInvoice.invoice_number}</span> • 
                {linkInvoice.supplier} • 
                <span className="font-mono font-bold ml-1">{formatCurrency(Math.abs(linkInvoice.total_amount || 0))}</span>
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={handleCancelLinkMode}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            {linkMode ? 'Select Transactions to Link' : 'Card Balance Dashboard'}
          </h1>
          <p className="text-muted-foreground">
            {linkMode 
              ? 'Select the transactions that match the statement, then click "Link to Statement"' 
              : 'Track pending payments from fuel card companies'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <Store className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {fuelStores.map(store => (
                <SelectItem key={store.id} value={store.id}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Removed - using sticky bar at bottom instead */}
        </div>
      </div>
      
      {/* Sticky Selected Total Bar - wider design at bottom */}
      {selectedTransactions.length > 0 && (() => {
        const totalAmt = transactionDetails.totalAmount;
        const exVat = totalAmt / 1.2;
        const vat = totalAmt - exVat;
        
        // Calculate expected settlement including fees
        // Bunkering: +handling fee
        // Key Fuels: +handling fee, -merchant fees
        let expectedSettlement = totalAmt;
        if (transactionDetails.primaryCategory === 'bunkering') {
          expectedSettlement = totalAmt + settlementFees.handling_fee;
        } else if (transactionDetails.primaryCategory === 'key_fuels') {
          expectedSettlement = totalAmt + settlementFees.handling_fee - settlementFees.merchant_fee_fuel - settlementFees.merchant_fee_shop;
        }
        
        // Calculate variance when in link mode (statement vs expected settlement)
        const creditNoteTotal = linkMode && linkInvoice ? Math.abs(linkInvoice.total_amount || 0) : 0;
        const variance = creditNoteTotal - expectedSettlement;
        
        return (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t-2 border-primary/20 shadow-2xl py-3 px-6">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-primary" />
                  <span className="text-sm text-gray-600">{selectedTransactions.length} selected</span>
                </div>
                
                {/* Link Mode: Show Selected Total, Expected Settlement, Credit Note, Variance */}
                {linkMode && linkInvoice ? (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="bg-blue-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-600">Txn Total: </span>
                      <span className="font-bold text-blue-600">{formatCurrency(totalAmt)}</span>
                    </div>
                    <div className="bg-cyan-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-600">Expected: </span>
                      <span className="font-bold text-cyan-600">{formatCurrency(expectedSettlement)}</span>
                    </div>
                    <div className="bg-purple-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-600">Statement: </span>
                      <span className="font-bold text-purple-600">{formatCurrency(creditNoteTotal)}</span>
                    </div>
                    <div className={`px-4 py-2 rounded-lg ${Math.abs(variance) < 0.01 ? 'bg-green-50' : 'bg-amber-50'}`}>
                      <span className="text-gray-600">Variance: </span>
                      <span className={`font-bold ${Math.abs(variance) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                        {formatCurrency(variance)}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* Normal Mode: VAT Breakdown */
                  <div className="flex items-center gap-4 text-sm bg-gray-50 px-4 py-2 rounded-lg">
                    <span className="text-gray-600">Net: <span className="font-semibold text-gray-800">{formatCurrency(exVat)}</span></span>
                    <span className="text-gray-400">+</span>
                    <span className="text-gray-600">VAT: <span className="font-semibold text-gray-800">{formatCurrency(vat)}</span></span>
                    <span className="text-gray-400">=</span>
                    <span className="text-primary font-bold text-lg">{formatCurrency(totalAmt)}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                {/* Fee inputs - shown in both normal and link mode */}
                {/* Bunkering: Handling Fee only */}
                {transactionDetails.primaryCategory === 'bunkering' && (
                  <div className="flex items-center gap-2 text-sm bg-green-50 px-3 py-2 rounded-lg">
                    <span className="text-green-700">+Fee £</span>
                    <Input
                      type="text"
                      className="w-16 h-8 px-2 text-sm font-bold text-green-700 bg-white border-green-300 text-center"
                      value={handlingFeeInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        setHandlingFeeInput(val);
                        const num = parseFloat(val);
                        if (!isNaN(num)) {
                          setSettlementFees(prev => ({ ...prev, handling_fee: num }));
                        }
                      }}
                      onBlur={() => {
                        const num = parseFloat(handlingFeeInput) || 0;
                        setHandlingFeeInput(num.toFixed(2));
                        setSettlementFees(prev => ({ ...prev, handling_fee: num }));
                      }}
                      data-testid="sticky-handling-fee"
                    />
                  </div>
                )}
                
                {/* Key Fuels: Fees */}
                {transactionDetails.primaryCategory === 'key_fuels' && (
                  <div className="flex items-center gap-3">
                    {transactionDetails.dieselVolume > 0 && (
                      <div className="flex items-center gap-2 text-sm bg-green-50 px-3 py-2 rounded-lg">
                        <span className="text-green-700">+Fee £</span>
                        <Input
                          type="text"
                          className="w-14 h-7 px-1 text-sm font-bold text-green-700 bg-white border-green-300 text-center"
                          value={handlingFeeInput}
                          onChange={(e) => {
                            setHandlingFeeInput(e.target.value);
                            const num = parseFloat(e.target.value);
                            if (!isNaN(num)) setSettlementFees(prev => ({ ...prev, handling_fee: num }));
                          }}
                          onBlur={() => {
                            const num = parseFloat(handlingFeeInput) || 0;
                            setHandlingFeeInput(num.toFixed(2));
                          }}
                        />
                      </div>
                    )}
                    {transactionDetails.nonDieselAmount > 0 && (
                      <div className="flex items-center gap-2 text-sm bg-red-50 px-3 py-2 rounded-lg">
                        <span className="text-red-600">-3% £</span>
                        <Input
                          type="text"
                          className="w-14 h-7 px-1 text-sm font-bold text-red-600 bg-white border-red-300 text-center"
                          value={merchantFeeFuelInput}
                          onChange={(e) => {
                            setMerchantFeeFuelInput(e.target.value);
                            const num = parseFloat(e.target.value);
                            if (!isNaN(num)) setSettlementFees(prev => ({ ...prev, merchant_fee_fuel: num }));
                          }}
                          onBlur={() => {
                            const num = parseFloat(merchantFeeFuelInput) || 0;
                            setMerchantFeeFuelInput(num.toFixed(2));
                          }}
                        />
                      </div>
                    )}
                    {transactionDetails.shopItemsAmount > 0 && (
                      <div className="flex items-center gap-2 text-sm bg-red-50 px-3 py-2 rounded-lg">
                        <span className="text-red-600">-10% £</span>
                        <Input
                          type="text"
                          className="w-14 h-7 px-1 text-sm font-bold text-red-600 bg-white border-red-300 text-center"
                          value={merchantFeeShopInput}
                          onChange={(e) => {
                            setMerchantFeeShopInput(e.target.value);
                            const num = parseFloat(e.target.value);
                            if (!isNaN(num)) setSettlementFees(prev => ({ ...prev, merchant_fee_shop: num }));
                          }}
                          onBlur={() => {
                            const num = parseFloat(merchantFeeShopInput) || 0;
                            setMerchantFeeShopInput(num.toFixed(2));
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {linkMode ? (
                  <Button 
                    onClick={handleLinkToStatement}
                    className="gap-2 h-10 px-6 bg-primary"
                    disabled={submitting}
                    data-testid="link-to-statement-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                    Link to Statement ({selectedTransactions.length})
                  </Button>
                ) : (
                  <Button 
                    onClick={() => setReceivePaymentOpen(true)}
                    className="gap-2 h-10 px-6"
                    data-testid="receive-payment-sticky-btn"
                  >
                    <Banknote className="h-4 w-4" />
                    Receive Payment
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-blue-500/5 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Non Bunkering</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(totals.non_bunkering)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-500/5 border-orange-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Fuel className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bunkering</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(totals.bunkering)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-500/5 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Fuel className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Key Fuels</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totals.key_fuels)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-500/5 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">AMEX</p>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(totals.amex)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-emerald-500/5 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CreditCard className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Voucher</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(totals.voucher)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-indigo-500/5 border-indigo-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <PoundSterling className="h-5 w-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Outstanding</p>
                <p className="text-xl font-bold text-indigo-600">{formatCurrency(totals.grand_total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs: Outstanding Balances & Settlement History */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="balances" className="flex items-center gap-2" data-testid="tab-balances">
            <CreditCard className="h-4 w-4" />
            Outstanding Balances
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
            <History className="h-4 w-4" />
            Settlement History
          </TabsTrigger>
        </TabsList>
        
        {/* Outstanding Balances Tab */}
        <TabsContent value="balances" className="mt-4">
          {loading ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">Loading balances...</p>
              </CardContent>
            </Card>
          ) : balances.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                <h2 className="text-xl font-semibold mb-2">All Settled!</h2>
                <p className="text-muted-foreground">
                  No outstanding fuel card balances for this store.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Category Filter */}
              <div className="flex gap-2">
                <Button 
                  variant={selectedCategory === 'all' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory('all')}
                >
                  All
                </Button>
                <Button 
                  variant={selectedCategory === 'non_bunkering' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory('non_bunkering')}
                  className="text-blue-600"
                >
                  Non Bunkering
                </Button>
                <Button 
                  variant={selectedCategory === 'bunkering' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory('bunkering')}
                  className="text-orange-600"
                >
                  Bunkering
                </Button>
                <Button 
                  variant={selectedCategory === 'key_fuels' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory('key_fuels')}
                  className="text-green-600"
                >
                  Key Fuels
                </Button>
                <Button 
                  variant={selectedCategory === 'amex' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory('amex')}
                  className="text-purple-600"
                >
                  AMEX
                </Button>
                <Button 
                  variant={selectedCategory === 'voucher' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory('voucher')}
                  className="text-emerald-600"
                >
                  Voucher
                </Button>
              </div>
              
              {/* Transaction Lists by Category */}
              {(selectedCategory === 'all' || selectedCategory === 'non_bunkering') && renderCategorySection('non_bunkering')}
              {(selectedCategory === 'all' || selectedCategory === 'bunkering') && renderCategorySection('bunkering')}
              {(selectedCategory === 'all' || selectedCategory === 'key_fuels') && renderCategorySection('key_fuels')}
              {(selectedCategory === 'all' || selectedCategory === 'amex') && renderCategorySection('amex')}
              {(selectedCategory === 'all' || selectedCategory === 'voucher') && renderCategorySection('voucher')}
            </div>
          )}
        </TabsContent>
        
        {/* Settlement History Tab */}
        <TabsContent value="history" className="mt-4">
          {settlements.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-xl font-semibold mb-2">No Settlements Yet</h2>
                <p className="text-muted-foreground">
                  Settlement history will appear here once payments are received.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <History className="h-5 w-5" />
                  All Settlements
                </CardTitle>
                <CardDescription>
                  Click on a settlement to view transaction details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {settlements.map(settlement => (
                    <div 
                      key={settlement.id} 
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50 hover:bg-slate-50 hover:border-primary/30 transition-all cursor-pointer"
                      onClick={() => fetchSettlementDetail(settlement.id)}
                      data-testid={`settlement-row-${settlement.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          settlement.card_category === 'bunkering' ? 'bg-orange-500/20' :
                          settlement.card_category === 'key_fuels' ? 'bg-green-500/20' :
                          settlement.card_category === 'non_bunkering' ? 'bg-blue-500/20' :
                          'bg-purple-500/20'
                        }`}>
                          {settlement.card_category === 'bunkering' || settlement.card_category === 'key_fuels' ? (
                            <Fuel className={`h-5 w-5 ${
                              settlement.card_category === 'bunkering' ? 'text-orange-500' : 'text-green-500'
                            }`} />
                          ) : (
                            <CreditCard className={`h-5 w-5 ${
                              settlement.card_category === 'non_bunkering' ? 'text-blue-500' : 'text-purple-500'
                            }`} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{settlement.card_names?.join(', ')}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">
                              {settlement.card_category?.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {settlement.period_start === settlement.period_end 
                              ? settlement.period_start 
                              : `${settlement.period_start} to ${settlement.period_end}`
                            }
                            <span className="text-muted-foreground/50">•</span>
                            <span>{settlement.transaction_ids?.length || 0} transaction(s)</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatCurrency(settlement.statement_amount)}</p>
                          <p className="text-xs text-muted-foreground">
                            Ref: {settlement.statement_reference}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" className="gap-1">
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Receive Payment Dialog */}
      <Dialog open={receivePaymentOpen} onOpenChange={setReceivePaymentOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Banknote className="h-5 w-5 text-green-500" />
              Receive Fuel Card Payment
            </DialogTitle>
            <DialogDescription>
              Record payment received from fuel card company
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Selected Transactions Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Selected Transactions:</span>
                <span className="font-medium">{selectedTransactions.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Card(s):</span>
                <span className="font-medium">{transactionDetails.cardNames.join(', ') || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Category:</span>
                <span className="font-medium capitalize">{transactionDetails.primaryCategory?.replace('_', ' ') || '-'}</span>
              </div>
            </div>

            {/* Bunkering Settlement Calculation */}
            {transactionDetails.primaryCategory === 'bunkering' && (
              <Card className="border-orange-500/30 bg-orange-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-orange-600">
                    <Calculator className="h-4 w-4" />
                    Bunkering Settlement Calculation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between bg-background/50 p-2 rounded">
                      <span className="text-muted-foreground">Total Amount (Cost):</span>
                      <span className="font-mono font-bold">{formatCurrency(transactionDetails.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between bg-background/50 p-2 rounded">
                      <span className="text-muted-foreground">Total Volume:</span>
                      <span className="font-mono font-bold">{transactionDetails.totalVolume.toFixed(2)} L</span>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <Edit2 className="h-3 w-3" />
                      Handling Fee (Editable)
                    </Label>
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          className="pl-7 font-mono"
                          value={settlementFees.handling_fee.toFixed(2)}
                          onChange={(e) => setSettlementFees({
                            ...settlementFees,
                            handling_fee: parseFloat(e.target.value) || 0
                          })}
                          data-testid="handling-fee-input"
                        />
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        = {transactionDetails.totalVolume.toFixed(2)} L × {((transactionDetails.cardConfig?.handling_fee_per_litre || 0)).toFixed(2)}p/L
                      </span>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between text-lg font-bold bg-orange-500/10 p-3 rounded-lg">
                    <span>Settlement Amount:</span>
                    <span className="text-orange-600">{formatCurrency(calculatedSettlement)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key Fuels Settlement Calculation */}
            {transactionDetails.primaryCategory === 'key_fuels' && (
              <Card className="border-green-500/30 bg-green-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-green-600">
                    <Calculator className="h-4 w-4" />
                    Key Fuels Settlement Calculation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Amount Breakdown */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between bg-background/50 p-2 rounded">
                      <span className="text-muted-foreground">Diesel Amount ({transactionDetails.dieselVolume.toFixed(2)}L at Cost):</span>
                      <span className="font-mono font-bold">{formatCurrency(transactionDetails.dieselAmount)}</span>
                    </div>
                    <div className="flex justify-between bg-background/50 p-2 rounded">
                      <span className="text-muted-foreground">Non-Diesel Amount ({transactionDetails.nonDieselVolume.toFixed(2)}L at Retail):</span>
                      <span className="font-mono font-bold">{formatCurrency(transactionDetails.nonDieselAmount)}</span>
                    </div>
                    <div className="flex justify-between bg-background/50 p-2 rounded">
                      <span className="text-muted-foreground">Shop Items Amount:</span>
                      <span className="font-mono font-bold">{formatCurrency(transactionDetails.shopItemsAmount)}</span>
                    </div>
                    <div className="flex justify-between bg-green-500/10 p-2 rounded font-medium">
                      <span>Subtotal:</span>
                      <span className="font-mono">{formatCurrency(transactionDetails.dieselAmount + transactionDetails.nonDieselAmount + transactionDetails.shopItemsAmount)}</span>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Editable Fees */}
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm text-green-700">
                        <Edit2 className="h-3 w-3" />
                        Handling Fee (Editable)
                      </Label>
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">+ £</span>
                          <Input
                            type="number"
                            step="0.01"
                            className="pl-10 font-mono"
                            value={settlementFees.handling_fee.toFixed(2)}
                            onChange={(e) => setSettlementFees({
                              ...settlementFees,
                              handling_fee: parseFloat(e.target.value) || 0
                            })}
                            data-testid="key-fuels-handling-fee"
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          = {(transactionDetails.dieselVolume + transactionDetails.nonDieselVolume).toFixed(2)} L × {(transactionDetails.cardConfig?.diesel_handling_fee_per_litre || 0).toFixed(2)}p/L
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm text-red-600">
                        <Edit2 className="h-3 w-3" />
                        Merchant Fee - Non-Diesel ({transactionDetails.cardConfig?.other_grades_merchant_fee_percent || 3}%)
                      </Label>
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">- £</span>
                          <Input
                            type="number"
                            step="0.01"
                            className="pl-10 font-mono"
                            value={settlementFees.merchant_fee_fuel.toFixed(2)}
                            onChange={(e) => setSettlementFees({
                              ...settlementFees,
                              merchant_fee_fuel: parseFloat(e.target.value) || 0
                            })}
                            data-testid="merchant-fee-fuel"
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          = {formatCurrency(transactionDetails.nonDieselAmount)} × {transactionDetails.cardConfig?.other_grades_merchant_fee_percent || 3}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm text-red-600">
                        <Edit2 className="h-3 w-3" />
                        Merchant Fee - Shop Items ({transactionDetails.cardConfig?.shop_items_merchant_fee_percent || 10}%)
                      </Label>
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">- £</span>
                          <Input
                            type="number"
                            step="0.01"
                            className="pl-10 font-mono"
                            value={settlementFees.merchant_fee_shop.toFixed(2)}
                            onChange={(e) => setSettlementFees({
                              ...settlementFees,
                              merchant_fee_shop: parseFloat(e.target.value) || 0
                            })}
                            data-testid="merchant-fee-shop"
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          = {formatCurrency(transactionDetails.shopItemsAmount)} × {transactionDetails.cardConfig?.shop_items_merchant_fee_percent || 10}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between text-lg font-bold bg-green-500/10 p-3 rounded-lg">
                    <span>Settlement Amount:</span>
                    <span className="text-green-600">{formatCurrency(calculatedSettlement)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Non-Bunkering / AMEX / Voucher - Simple Display */}
            {(transactionDetails.primaryCategory === 'non_bunkering' || 
              transactionDetails.primaryCategory === 'amex' || 
              transactionDetails.primaryCategory === 'voucher') && (
              <div className="flex justify-between border-t pt-2 mt-2 bg-muted/50 p-4 rounded-lg">
                <span className="font-medium">Expected Total (Retail):</span>
                <span className="text-lg font-bold text-blue-600">{formatCurrency(calculatedSettlement)}</span>
              </div>
            )}

            {/* Payment Date */}
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                data-testid="payment-date"
              />
            </div>

            {/* Statement Reference */}
            <div className="space-y-2">
              <Label>Statement Reference <span className="text-red-500">*</span></Label>
              <Input
                placeholder="e.g., INV-2026-001234"
                value={paymentForm.statement_reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, statement_reference: e.target.value })}
                data-testid="statement-reference"
              />
            </div>

            {/* Statement Amount */}
            <div className="space-y-2">
              <Label>Statement Amount <span className="text-red-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  className="pl-7 font-mono"
                  value={paymentForm.statement_amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, statement_amount: e.target.value })}
                  data-testid="statement-amount"
                />
              </div>
              
              {/* Variance indicator against calculated settlement */}
              {paymentForm.statement_amount && (
                <div className={`text-sm px-3 py-2 rounded ${
                  Math.abs(parseFloat(paymentForm.statement_amount) - calculatedSettlement) < 0.01 
                    ? 'bg-green-500/10 text-green-600' 
                    : 'bg-yellow-500/10 text-yellow-600'
                }`}>
                  Variance from calculated: {formatCurrency(parseFloat(paymentForm.statement_amount) - calculatedSettlement)}
                  {Math.abs(parseFloat(paymentForm.statement_amount) - calculatedSettlement) < 0.01 && ' ✓'}
                </div>
              )}
            </div>

            {/* Bank Reference (Optional) */}
            <div className="space-y-2">
              <Label>Bank Reference (Optional)</Label>
              <Input
                placeholder="e.g., TRN-12345678"
                value={paymentForm.bank_reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, bank_reference: e.target.value })}
                data-testid="bank-reference"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReceivePaymentOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReceivePayment} 
              disabled={submitting}
              data-testid="confirm-payment-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settlement Detail Dialog */}
      <Dialog open={settlementDetailOpen} onOpenChange={setSettlementDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <FileText className="h-5 w-5 text-primary" />
              Settlement Details
            </DialogTitle>
            <DialogDescription>
              View settled transactions and payment information
            </DialogDescription>
          </DialogHeader>
          
          {settlementDetailLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">Loading settlement details...</p>
            </div>
          ) : selectedSettlement && (
            <div className="space-y-6">
              {/* Settlement Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-primary/5">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Statement Amount</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(selectedSettlement.statement_amount)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Calculated Total</p>
                    <p className="text-xl font-semibold">{formatCurrency(selectedSettlement.calculated_total)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Variance</p>
                    <p className={`text-xl font-semibold ${
                      Math.abs(selectedSettlement.variance || 0) < 0.01 ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {formatCurrency(selectedSettlement.variance || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Transactions</p>
                    <p className="text-xl font-semibold">{selectedSettlement.transactions?.length || 0}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Settlement Info */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Card(s):</span>
                      <p className="font-medium">{selectedSettlement.card_names?.join(', ')}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <p className="font-medium capitalize">{selectedSettlement.card_category?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Payment Date:</span>
                      <p className="font-medium">{selectedSettlement.payment_date}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Period:</span>
                      <p className="font-medium">
                        {selectedSettlement.period_start === selectedSettlement.period_end 
                          ? selectedSettlement.period_start 
                          : `${selectedSettlement.period_start} to ${selectedSettlement.period_end}`
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Statement Ref:</span>
                      <p className="font-medium font-mono">{selectedSettlement.statement_reference}</p>
                    </div>
                    {selectedSettlement.bank_reference && (
                      <div>
                        <span className="text-muted-foreground">Bank Ref:</span>
                        <p className="font-medium font-mono">{selectedSettlement.bank_reference}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Fee Breakdown for Bunkering/Key Fuels */}
                  {selectedSettlement.settlement_fees && (
                    <div className="pt-3 border-t">
                      <p className="text-sm font-medium mb-2">Fee Breakdown:</p>
                      <div className="flex gap-4 text-sm">
                        {selectedSettlement.settlement_fees.handling_fee > 0 && (
                          <span className="text-green-600">
                            + Handling Fee: {formatCurrency(selectedSettlement.settlement_fees.handling_fee)}
                          </span>
                        )}
                        {selectedSettlement.settlement_fees.merchant_fee_fuel > 0 && (
                          <span className="text-red-600">
                            - Merchant Fee (Fuel): {formatCurrency(selectedSettlement.settlement_fees.merchant_fee_fuel)}
                          </span>
                        )}
                        {selectedSettlement.settlement_fees.merchant_fee_shop > 0 && (
                          <span className="text-red-600">
                            - Merchant Fee (Shop): {formatCurrency(selectedSettlement.settlement_fees.merchant_fee_shop)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Transactions Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Settled Transactions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedSettlement.transactions?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-muted-foreground">
                            <th className="text-left py-2 px-2">Date</th>
                            <th className="text-left py-2 px-2">Card</th>
                            {selectedSettlement.card_category === 'bunkering' && (
                              <>
                                <th className="text-right py-2 px-2">Volume</th>
                                <th className="text-right py-2 px-2">Retail</th>
                              </>
                            )}
                            {selectedSettlement.card_category === 'key_fuels' && (
                              <>
                                <th className="text-right py-2 px-2">Diesel</th>
                                <th className="text-right py-2 px-2">Non-Diesel</th>
                                <th className="text-right py-2 px-2">Shop</th>
                              </>
                            )}
                            <th className="text-right py-2 px-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSettlement.transactions.map((txn, idx) => (
                            <tr key={txn.id || idx} className="border-b border-border/50 hover:bg-slate-50">
                              <td className="py-2 px-2">{txn.sales_date}</td>
                              <td className="py-2 px-2">{txn.card_name}</td>
                              {selectedSettlement.card_category === 'bunkering' && (
                                <>
                                  <td className="text-right py-2 px-2 font-mono">
                                    {(txn.total_volume || 0).toFixed(2)} L
                                  </td>
                                  <td className="text-right py-2 px-2 font-mono text-muted-foreground">
                                    {formatCurrency(txn.retail_amount || txn.total_amount)}
                                  </td>
                                </>
                              )}
                              {selectedSettlement.card_category === 'key_fuels' && (
                                <>
                                  <td className="text-right py-2 px-2 font-mono text-blue-600">
                                    {txn.diesel_amount > 0 ? formatCurrency(txn.diesel_amount) : '-'}
                                  </td>
                                  <td className="text-right py-2 px-2 font-mono text-amber-600">
                                    {txn.non_diesel_amount > 0 ? formatCurrency(txn.non_diesel_amount) : '-'}
                                  </td>
                                  <td className="text-right py-2 px-2 font-mono text-purple-600">
                                    {txn.shop_items_amount > 0 ? formatCurrency(txn.shop_items_amount) : '-'}
                                  </td>
                                </>
                              )}
                              <td className="text-right py-2 px-2 font-mono font-semibold">
                                {formatCurrency(txn.expected_payment || txn.total_amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-muted/50 font-semibold">
                            <td colSpan={selectedSettlement.card_category === 'bunkering' ? 3 : selectedSettlement.card_category === 'key_fuels' ? 4 : 1} className="py-2 px-2 text-right">
                              Total:
                            </td>
                            <td className="text-right py-2 px-2 font-mono text-lg">
                              {formatCurrency(selectedSettlement.calculated_total)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      Transaction details not available for this settlement.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettlementDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelCardBalancesPage;
